import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Container,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNotification } from "app/NotificationProvider";
import {
  BottomSheetHeader,
  ConfirmCancellationDialog,
} from "common/components";
import { AnimalGroupWeightIcon, GrassIcon } from "common/icons";
import { useTranslation } from "common/locales";
import { addMonths } from "date-fns";
import { FieldArray, Form, Formik, FormikErrors } from "formik";
import { cloneDeep } from "lodash";
import {
  DEFAULT_STOCK_CLASS_NAME,
  DefaultStockClass,
  StockClassBirthingPeriod,
  StockClassFeedAllocation,
  StockClassWithBirthingPeriods,
  useDefaultStockClasses,
} from "model";
import {
  useAnimalGroup,
  useCurrentFarmAnimalGroups,
} from "model/useAnimalGroupQueries";
import { useCurrentFarm } from "model/useFarmQueries";
import { useBirthingPeriodsByIds } from "model/useStockClassBirthingPeriodQueries";
import { useFeedAllocationsByIds } from "model/useStockClassFeedAllocationQueries";
import { useStockClassesByIds } from "model/useStockClassQueries";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createAnimalGroup,
  createStockClass,
  createStockClassBirthingPeriod,
  deleteStockClass,
  deleteStockClassBirthingPeriod,
  deleteStockClassFeedAllocations,
  fetchstockClassesById,
  fetchStockClassFeedAllocationsById,
  regenerateFeedingAllocation,
  updateAnimalGroup,
  updateStockClass,
  updateStockClassBirthingPeriod,
} from "services";
import { useAppStore } from "stores";
import * as Yup from "yup";
import { AnalyticsEvents, mixpanel } from "../../common/analytics";
import { feedBudgetPlannerEnabledForUserAndAnimalGroup } from "../../common/featureToggle";
import RegenerateFeedAllocationDialog from "./RegenerateFeedAllocationDialog";
import StockClassInput from "./StockClassInput";

const GUID_TO_BE_CREATED = "GUID_TO_BE_CREATED";
const EMPTY_ARRAY: never[] = [];

export default function AnimalGroupInput() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [
    regenerateFeedAllocationDialogOpen,
    setRegenerateFeedAllocationDialogOpen,
  ] = useState(false);
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { data: currentFarm } = useCurrentFarm();
  const { isLoading } = useCurrentFarmAnimalGroups();

  const { animalGroupId } = useParams<{ animalGroupId: string }>();
  const { data: animalGroup } = useAnimalGroup(animalGroupId);

  // Get stock classes for the animal group
  const { data: stockClassesRaw = EMPTY_ARRAY } = useStockClassesByIds(
    animalGroup?.stockClasses
  );

  // Collect all birthing period IDs and feed allocation IDs from stock classes
  const birthingPeriodIds = useMemo(
    () => stockClassesRaw.flatMap((sc) => sc.birthingPeriods || []),
    [stockClassesRaw]
  );
  const feedAllocationIds = useMemo(
    () => stockClassesRaw.flatMap((sc) => sc.feedAllocations || []),
    [stockClassesRaw]
  );

  // Fetch birthing periods and feed allocations
  const { data: birthingPeriods = EMPTY_ARRAY } = useBirthingPeriodsByIds(
    birthingPeriodIds,
    birthingPeriodIds.length > 0
  );
  const { data: feedAllocations = EMPTY_ARRAY } = useFeedAllocationsByIds(
    feedAllocationIds,
    feedAllocationIds.length > 0
  );

  // Combine stock classes with their birthing periods
  const stockClasses: StockClassWithBirthingPeriods[] = useMemo(() => {
    return stockClassesRaw.map((sc) => ({
      ...sc,
      birthingPeriodValues: birthingPeriods.filter((bp) =>
        sc.birthingPeriods?.includes(bp.guid)
      ),
    }));
  }, [stockClassesRaw, birthingPeriods]);

  const user = useAppStore((state) => state.user);
  const { data: defaultStockClasses = [] } = useDefaultStockClasses();

  const defaultStockClassesWithBirthingAbilityNames = useMemo(
    () =>
      defaultStockClasses.filter((d) => d.birthingAbility).map((d) => d.name),
    [defaultStockClasses]
  );

  const feedBudgetPlannerEnabled =
    feedBudgetPlannerEnabledForUserAndAnimalGroup(user, animalGroup);

  // Compute demand from feed allocations inline
  const feedAllocationDemand = useMemo(() => {
    let demand = 0;
    const now = new Date().valueOf();

    for (const sc of stockClasses) {
      const feedAllocationsForStockClass = feedAllocations.filter(
        (fa: StockClassFeedAllocation) =>
          sc.feedAllocations?.includes(fa.guid) &&
          new Date(fa.dateStart).valueOf() <= now
      );

      feedAllocationsForStockClass.sort(
        (a: StockClassFeedAllocation, b: StockClassFeedAllocation) =>
          new Date(b.dateStart).valueOf() - new Date(a.dateStart).valueOf()
      );

      const currentFeedAllocation = feedAllocationsForStockClass.at(0);
      if (currentFeedAllocation) {
        demand +=
          sc.animalCount *
          sc.averageAnimalWeightKg *
          currentFeedAllocation.allocatedBodyWeightFraction;
      }
    }
    return demand;
  }, [stockClasses, feedAllocations]);

  const validNumber = (min = 0) =>
    Yup.number()
      .typeError(t("edit.minNumber.error", { min }))
      .min(min, t("edit.minNumber.error", { min }))
      .required(t("edit.required.error"));

  const AnimalGroupSchema = Yup.object().shape({
    name: Yup.string().required(t("edit.required.error")),
    stockClasses: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(t("edit.required.error")),
        animalCount: validNumber(1),
        averageAnimalWeightKg: validNumber(1),
        defaultStockClassName: Yup.string(),
        consumptionKgPerDay: Yup.number().when(() => {
          if (feedBudgetPlannerEnabled) {
            return Yup.number().optional(); // hidden by default
          }

          return Yup.number()
            .typeError(t("edit.minNumber.error", { min: 0.1 }))
            .min(0.1, t("edit.minNumber.error", { min: 0.1 }))
            .required();
        }),
        birthingPeriodValues: Yup.array().when(
          ["defaultStockClassName", "animalCount"],
          (values: string[]): Yup.AnySchema => {
            // validate birthing periods only for stock classes that can give birth
            if (
              feedBudgetPlannerEnabled &&
              defaultStockClassesWithBirthingAbilityNames.includes(
                values[0] as DEFAULT_STOCK_CLASS_NAME
              )
            ) {
              return Yup.array()
                .of(
                  Yup.object().shape({
                    dateStart: Yup.date().required(t("edit.required.error")),
                    dateEnd: Yup.date().required(t("edit.required.error")),
                    expectedNumAnimalsWithoutPregnancy: validNumber(0).max(
                      parseInt(values[1], 10),
                      t("edit.maxNumber.error", { max: values[1] })
                    ),
                    expectedNumOffspringPerAnimal: validNumber(1).max(
                      32,
                      t("edit.maxNumber.error", { max: 32 })
                    ),
                  })
                )
                .required(t("edit.required.error"))
                .min(1, t("edit.required.error"));
            }

            return Yup.array();
          }
        ),
      })
    ),
  });

  const { notify } = useNotification();

  const [deletedStockClasses, setDeletedStockClasses] = useState<string[]>([]);

  const newStockClass = {
    name: "",
    guid: GUID_TO_BE_CREATED,
    animalGroup: animalGroup?.guid ?? "",
    animalCount: 0,
    averageAnimalWeightKg: 0,
    consumptionKgPerDay: 0,
    defaultStockClassName: "DAIRY_COW",
    birthingPeriods: [GUID_TO_BE_CREATED],
    birthingPeriodValues: [
      {
        guid: GUID_TO_BE_CREATED,
        stockClassGuid: GUID_TO_BE_CREATED,
        dateStart: new Date().toISOString(),
        dateEnd: new Date().toISOString(),
        expectedNumAnimalsWithoutPregnancy: 0,
        expectedNumOffspringPerAnimal: 0,
      },
    ],
  };

  // If there aren't any stock classes yet,
  // we create a default stock class for the user to adapt

  const [initialStockClassValues, setInitialStockClassValues] = useState<{
    name: string;
    stockClasses: Array<StockClassWithBirthingPeriods>;
  }>({
    name: "",
    stockClasses: [cloneDeep(newStockClass)],
  });

  const nameInputRef = useRef<HTMLInputElement>(null);

  // Track which animalGroupId we've already initialized form values for.
  // This prevents background refetches (which update dataUpdatedAt but not
  // the actual data) from resetting the form while the user is editing.
  const initializedForId = useRef<string | null>(null);

  useEffect(() => {
    // Skip for add mode — default values are already set in useState initializer.
    if (!animalGroupId) return;
    // Already initialized for this animal group — don't overwrite user edits.
    if (initializedForId.current === animalGroupId) return;
    // Wait for the animal group to load.
    if (!animalGroup) return;
    // Wait for stock classes to load (if the AG has any).
    if (animalGroup.stockClasses.length > 0 && stockClasses.length === 0)
      return;

    initializedForId.current = animalGroupId;
    setInitialStockClassValues({
      name: animalGroup?.name ?? "",
      stockClasses:
        stockClasses && stockClasses.length !== 0
          ? stockClasses.map((stockClass) => ({
              ...stockClass,
              birthingPeriodValues:
                stockClass.birthingPeriodValues.length > 0
                  ? stockClass.birthingPeriodValues
                  : cloneDeep(newStockClass.birthingPeriodValues),
            }))
          : [cloneDeep(newStockClass)],
    });
  }, [animalGroupId, animalGroup, stockClasses, birthingPeriods]);

  useEffect(() => {
    // Focus the name input on mount
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const [formStockClassValues, setFormStockClassValues] = useState<{
    name: string;
    stockClasses: Array<StockClassWithBirthingPeriods>;
  }>({
    name: "",
    stockClasses: [cloneDeep(newStockClass)],
  });

  const handleBackToFarm = () => {
    if (currentFarm?.guid) {
      navigate(`/farm/${currentFarm.guid}`);
    }
  };

  if (isLoading) {
    return <LinearProgress />;
  }
  return (
    <Box
      sx={{
        backgroundColor: isMobile ? "white" : "transparent",
        minHeight: "100%",
        paddingBottom: isMobile ? "100px" : 0,
      }}
    >
      {isMobile && (
        <BottomSheetHeader
          title={
            animalGroup
              ? t("edit.editingAnimalGroup")
              : t("edit.addingAnimalGroup")
          }
          onBack={handleBackToFarm}
        />
      )}
      <Formik
        enableReinitialize
        initialValues={initialStockClassValues}
        validationSchema={AnimalGroupSchema}
        validateOnChange={false} // disable validation every field change, otherwise would validate all fields
        validateOnBlur={false} // disable validation every field blur, otherwise would validate all fields
        onSubmit={(values) => {
          if (
            !animalGroup ||
            !feedBudgetPlannerEnabled // not asking for confirmation when creating animal group or if feed budget planner is not enabled
          ) {
            void handleSave(values.name, values.stockClasses);
          } else {
            setFormStockClassValues(values);
            setRegenerateFeedAllocationDialogOpen(true);
          }
        }}
      >
        {({ values, handleChange, setFieldValue, errors, validateField }) => {
          const handleInputChange = async (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            fieldName: string
          ) => {
            await (handleChange(e) as unknown as Promise<void>); // async, typings are wrong
            await (validateField(fieldName) as unknown as Promise<void>); // validate only changed field, async, typings are wrong
          };

          const groupWeight = values.stockClasses.reduce((acc, s) => {
            return (acc += s.averageAnimalWeightKg * s.animalCount);
          }, 0);

          let groupDemandInKg = values.stockClasses.reduce((acc, s) => {
            return (acc += s.consumptionKgPerDay * s.animalCount);
          }, 0);

          if (feedBudgetPlannerEnabled) {
            groupDemandInKg = feedAllocationDemand || 0;
          }

          const groupDemandInPercent =
            groupWeight > 0
              ? ((groupDemandInKg / groupWeight) * 100).toFixed(1)
              : 0;

          return (
            <>
              <Container
                sx={{
                  paddingTop: {
                    xs: "calc(env(safe-area-inset-top) + 16px)",
                    sm: "16px",
                  },
                  backgroundColor: isMobile ? "white" : "transparent",
                }}
              >
                <List>
                  <ListItem>
                    <Field
                      required
                      id="animal-group-input-name"
                      label={t("edit.animalGroupName.label")}
                      inputRef={nameInputRef}
                      value={values.name}
                      helperText={errors.name}
                      error={!!errors.name}
                      fullWidth
                      onChange={(e) => handleInputChange(e, "name")}
                      name={`name`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <AnimalGroupWeightIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${groupWeight} kg`}
                      secondary={t("edit.weight.label")}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <GrassIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("animalGroupInput.demand", {
                        groupDemandInKg: Math.round(groupDemandInKg),
                        groupDemandInPercent,
                      })}
                      secondary={t("edit.demand.label")}
                    />
                  </ListItem>
                </List>
              </Container>
              <Container
                style={{
                  textAlign: "left",
                  backgroundColor: isMobile ? "white" : "transparent",
                }}
              >
                <Typography variant="h6">
                  {t("animalGroupInput.stockClasses")}
                </Typography>
              </Container>
              <Container
                style={{
                  textAlign: "center",
                  backgroundColor: isMobile ? "white" : "transparent",
                }}
              >
                <Form id="animal-group-input-form">
                  <FieldArray name="stockClasses">
                    {({ remove, push }) => (
                      <>
                        {values.stockClasses.length > 0 &&
                          values.stockClasses.map((stockClass, index) => (
                            <StockClassInput
                              key={index}
                              handleInputChange={handleInputChange}
                              stockClass={stockClass}
                              deletedStockClasses={deletedStockClasses}
                              onDelete={(guid: string) => {
                                setDeletedStockClasses([
                                  ...deletedStockClasses,
                                  guid,
                                ]);
                              }}
                              remove={remove}
                              setFieldValue={setFieldValue}
                              index={index}
                              feedBudgetPlannerEnabled={
                                feedBudgetPlannerEnabled
                              }
                              errors={
                                errors?.stockClasses?.[index] as
                                  | FormikErrors<StockClassWithBirthingPeriods>
                                  | undefined
                              }
                              validateField={validateField}
                            />
                          ))}
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ marginBottom: "16px" }}
                          startIcon={<AddIcon />}
                          onClick={() => push(cloneDeep(newStockClass))}
                        >
                          {t("animalGroupInput.addStockClass")}
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </Form>
              </Container>
            </>
          );
        }}
      </Formik>
      <Divider />
      <ButtonContainer>
        <ButtonGroup>
          <Button color="secondary" onClick={handleCancel}>
            {t("edit.button.cancel")}
          </Button>
          <Button color="primary" type="submit" form="animal-group-input-form">
            {t("edit.button.save")}
          </Button>
        </ButtonGroup>
        <ConfirmCancellationDialog
          open={open}
          onClose={handleConfirmationClose}
        />
        <RegenerateFeedAllocationDialog
          open={regenerateFeedAllocationDialogOpen}
          onCancel={() => setRegenerateFeedAllocationDialogOpen(false)}
          onConfirm={() => {
            setRegenerateFeedAllocationDialogOpen(false);
            return handleSave(
              formStockClassValues.name,
              formStockClassValues.stockClasses
            );
          }}
        />
      </ButtonContainer>
    </Box>
  );

  function handleConfirmationClose() {
    setOpen(false);
  }

  function handleCancel() {
    setOpen(true);
  }

  function createUpdateDeleteBirthingPeriods(
    birthingPeriodValues: Array<StockClassBirthingPeriod>,
    stockClassGuid: string,
    selectedDefaultStockClass: DefaultStockClass
  ) {
    if (selectedDefaultStockClass.birthingAbility) {
      return Promise.all(
        birthingPeriodValues.map((birthingPeriod) => {
          if (birthingPeriod.guid === GUID_TO_BE_CREATED) {
            const { guid, ...payload } = { ...birthingPeriod, stockClassGuid }; // removing guid from stock class, not required when creating
            return createStockClassBirthingPeriod(
              payload as unknown as Partial<StockClassBirthingPeriod>
            );
          } else {
            const { stockClassGuid, ...payload } = { ...birthingPeriod }; // removing stockClassGuid from stock class, not required when updating
            return updateStockClassBirthingPeriod(
              payload as unknown as StockClassBirthingPeriod
            );
          }
        })
      );
    } else {
      // otherwise delete all because selected stock class without birthing ability
      return Promise.all(
        birthingPeriodValues.map((birthingPeriod) => {
          if (birthingPeriod.guid !== GUID_TO_BE_CREATED) {
            return deleteStockClassBirthingPeriod(birthingPeriod.guid);
          }
          return Promise.resolve();
        })
      );
    }
  }

  async function handleSave(
    name: string,
    stockClasses: Array<StockClassWithBirthingPeriods>
  ) {
    try {
      if (animalGroup) {
        await Promise.all(
          deletedStockClasses
            .filter((sc) => sc !== GUID_TO_BE_CREATED)
            .map((guid) => {
              return deleteStockClass(guid);
            })
        );

        // TODO: in MVP we support only single birthing period per stock class, therefore no need for deletion of birthing periods! Only when changing to default stock class without birthing ability

        // first create/update stock classes, then birthing periods
        await Promise.all(
          stockClasses.map(async (s) => {
            let stockClassGuid = s.guid;
            const selectedDefaultStockClass = defaultStockClasses.find(
              (d) => d.name === s.defaultStockClassName
            );
            if (s.guid === GUID_TO_BE_CREATED) {
              const stockClass = await createStockClass({
                ...s,
                animalGroup: animalGroup.guid,
              });

              stockClassGuid = stockClass.guid;
            } else {
              await updateStockClass(s);
            }
            if (feedBudgetPlannerEnabled) {
              if (selectedDefaultStockClass) {
                await createUpdateDeleteBirthingPeriods(
                  s.birthingPeriodValues,
                  stockClassGuid,
                  selectedDefaultStockClass
                );
              }

              s.birthingPeriodValues.sort(
                (bp1, bp2) =>
                  new Date(bp1.dateEnd).getTime() -
                  new Date(bp2.dateEnd).getTime()
              );

              const lastBirthingPeriod = s.birthingPeriodValues.at(-1);

              const newFeedAllocations = await regenerateFeedingAllocation({
                stockClassGuid,
                dateStart: new Date(),
                dateEnd: addMonths(
                  new Date(lastBirthingPeriod?.dateEnd || new Date()),
                  6
                ),
              });
              // removing all feed allocations for this stock class, because regenerate will delete most of those

              await deleteStockClassFeedAllocations(
                feedAllocations.map((f) => f.guid)
              );
            }
          })
        );

        const updatedAnimalGroup = await updateAnimalGroup({
          ...animalGroup,
          name,
        });

        if (feedBudgetPlannerEnabled) {
          const updatedStockClasses = await fetchstockClassesById(
            // reload stock classes
            updatedAnimalGroup.stockClasses
          );

          await fetchStockClassFeedAllocationsById(
            // reload all feed allocations
            updatedStockClasses.flatMap((sc) => sc.feedAllocations || [])
          );
          notify(
            t("animalGroupInput.feedAllocation.edit.success", {
              animalGroupName: name,
            })
          );
        } else {
          notify(t("animalGroupInput.edit.success", { animalGroupName: name }));
        }
        mixpanel.track(AnalyticsEvents.ANIMAL_GROUP_UPDATED);
        navigate(`/animal-group/${animalGroup?.guid}`);
      } else if (currentFarmId) {
        const newAnimalGroup = await createAnimalGroup({
          guid: GUID_TO_BE_CREATED,
          farm: currentFarmId,
          name,
          postGrazingTarget: 1500,
          rotationLength: 20,
          stockClasses: [],
          feedBudgetPlannerEnabled,
        });
        await Promise.all(
          stockClasses.map(async (sc) => {
            const stockClass = await createStockClass({
              ...sc,
              animalGroup: newAnimalGroup.guid,
            });
            const selectedDefaultStockClass = defaultStockClasses.find(
              (d) => d.name === sc.defaultStockClassName
            );
            if (
              feedBudgetPlannerEnabled &&
              sc.birthingPeriodValues.length > 0 &&
              selectedDefaultStockClass
            ) {
              await createUpdateDeleteBirthingPeriods(
                sc.birthingPeriodValues,
                stockClass.guid,
                selectedDefaultStockClass
              );

              sc.birthingPeriodValues.sort(
                (bp1, bp2) =>
                  new Date(bp1.dateEnd).getTime() -
                  new Date(bp2.dateEnd).getTime()
              );

              const lastBirthingPeriod = sc.birthingPeriodValues.at(-1);

              const newFeedAllocations = await regenerateFeedingAllocation({
                stockClassGuid: stockClass.guid,
                dateStart: new Date(),
                dateEnd: addMonths(
                  new Date(lastBirthingPeriod?.dateEnd ?? new Date()),
                  6
                ),
              });

              const newGuids = newFeedAllocations.feedingAllocations.map(
                (fa: StockClassFeedAllocation) => fa.guid
              );
              await fetchStockClassFeedAllocationsById(newGuids);
            }
          })
        );

        // generate feed allocation once

        mixpanel.track(AnalyticsEvents.ANIMAL_GROUP_CREATED);
        if (feedBudgetPlannerEnabled) {
          notify(
            t("animalGroupInput.feedAllocation.create.success", {
              animalGroupName: name,
            })
          );
        } else {
          notify(t("animalGroupInput.add.success", { animalGroupName: name }));
        }

        navigate(`/animal-group/${newAnimalGroup.guid}`);
      }
    } catch (e) {
      notify(t("animalGroupInput.boundary.error"), "error");
    }
  }
}

const ButtonGroup = styled("div")(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const ButtonContainer = styled(Container)({
  position: "relative",
  flex: "0 0 auto",
  display: "block",
  height: "70px",
  width: "100%",
  background: "white",
});

const Field = styled(TextField)(({ theme }) => ({
  minWidth: "300px",
  paddingBottom: theme.spacing(2),
}));
